import React, {
  useEffect, useContext, useState, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Typography from '@digital-hig/mui/dist/Typography';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import DOMPurify from 'isomorphic-dompurify';
import Tabs from '@digital-hig/mui/dist/Tabs';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import { window, document } from 'global';
import Skeleton from 'react-loading-skeleton';
import Button from '@mui/material/Button';
import loadable from '@loadable/component';
import PageWrapper from '../components/pageWrapperHOC';
import IndexEdCard from '../components/indexEdCard';
import { LoginClickHandler } from '../common/login';
import { triggerManualLinkClick, triggerGenericPageView, triggerContentImpression } from '../common/analyticsApi';
import {
  setInitiatedSearch,
  getSelectedProduct,
  hasCertifications,
  getProductList,
  hasEducators,
  fetchIndexData,
  getEditorialData,
  getIndustrySubSegments,
} from '../store/indexSlice';

import {
  ENVIRONMENT,
  styleVariables,
  VERSION,
  EDC_SLUG_PERSONALIZED_EDUCATORS,
  EDC_SLUG_PERSONALIZED_TUTORIALS,
  EDC_SLUG_PERSONALIZED_COURSES,
  EDC_SLUG_PERSONALIZED_CERTIFICATIONS,
  EDC_SLUG_PERSONALIZED_COLLECTIONS,
  ERROR_TYPE_XHR,
  REQUEST_FLAGS,
  EDC_SLUG_INDEX_INDUSTRY,
  EDC_SLUG_INDEX_MORE_LEARNING_BLOCK1,
  EDC_SLUG_INDEX_MORE_LEARNING_BLOCK2,
  EDC_SLUG_INDEX_MORE_LEARNING_BLOCK3,
  PERSONALIZATION_EXPERIMENT_CONTROL,
  PERSONALIZATION_EXPERIMENT_TREATMENT,
  EDC_SLUG_INDEX_HEADER,
  EDC_SLUG_INDEX_WAYFINDING_BLOCK1,
  EDC_SLUG_INDEX_WAYFINDING_BLOCK2,
  EDC_SLUG_INDEX_WAYFINDING_BLOCK3,
  EDC_SLUG_INDEX_WAYFINDING_BLOCK4,
  EDC_SLUG_INDEX_WAYFINDING_INTRO,
  EDC_SLUG_INDEX_COLLECTIONS,
  EDC_SLUG_INDEX_TUTORIALS,
  EDC_SLUG_INDEX_COURSES,
  EDC_SLUG_INDEX_CERTIFICATIONS,
  EDC_SLUG_INDEX_EDUCATORS,
  EDC_SLUG_CATALOG_INTRO,
  EDC_SLUG_PERSONALIZED_SELECT_PRODUCT,
  PERSONALIZATION_EXPERIMENT_ACTIVETRIALONLY,
  PERSONALIZATION_EXPERIMENT_ACTIVETRIALANDCOMM,
  IRLCP_EXPERIMENT_CONTROL,
  IRLCP_EXPERIMENT_TREATMENT,
  INDUSTRY_RELEVANT_LEARNING_AND_CERT_PREP,
} from '../common/constants';
import EnvContext from '../common/envContext';
import { getChildProperty } from '../common/object';
import {
  fetchAllPersonalizedContent,
  fetchLearningRecords,
  getIndexContent,
  getLearningRecords,
  getUserDetails,
  isAuthChecked,
  isPersonalizedContentLoading,
  isUserProfileFetched,
  getIndustryFilter,
  getCapabilitiesFilter,
  getExperimentationContext,
  userTrialEntitlements,
} from '../store/userSlice';
import SearchBox from '../components/searchBox';
import {
  clearFilters, clearResults,
} from '../store/catalogSlice';
import IndexMoreLearning from '../components/indexMoreLearning';
import IndexRecentActivity from '../components/indexRecentActivity';
import GetStartedPanel from '../components/gettingStartedPanel';
import { getExperimentationSegment, getRecentSearchTerms } from '../common/utilities';
import SoftwareSelector from '../components/softwareSelector';
import { setErrorState } from '../store/errorSlice';
import logger from '../common/logger';
import DiscoverIndustryPanels from '../components/discoverIndustryPanels';

const sortByChunkSize = (firstArr = [], secondArr = [], chunkSize = 3, maxLength = 20) => {
  const maxLen = Math.max(firstArr.length, secondArr.length);
  const chunks = Array.from({ length: Math.ceil(maxLen / chunkSize) }, (_, i) => i * chunkSize);

  const result = chunks.reduce((acc, start) => {
    acc.push(...firstArr.slice(start, start + chunkSize));
    acc.push(...secondArr.slice(start, start + chunkSize));
    return acc;
  }, []);

  return result.slice(0, maxLength);
};

const Index = () => {
  const IndexCarouselSection = loadable(() => import(/* webpackChunkName: "indexCarouselSection" */'../components/indexCarouselSection'));
  const IndexIndustry = loadable(() => import(/* webpackChunkName: "indexIndustry" */'../components/indexIndustry'));
  const IndexEdBlock = loadable(() => import(/* webpackChunkName: "indexEdBlock" */'../components/indexEdBlock'));

  const indexData = useSelector(getEditorialData);
  const dispatch = useDispatch();
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const {
    userid, first, entitlements, oa2Token,
  } = useSelector(getUserDetails);
  const { isPreview } = envContext[REQUEST_FLAGS] || false;
  const { personalizedData = {} } = useSelector((state) => state.userData);
  const {
    collections, modules, tutorials, curated, courses, quickStarts, filters,
  } = personalizedData;
  const authChecked = useSelector(isAuthChecked);
  const appVersion = envContext[VERSION];
  const productSelected = useSelector(getSelectedProduct);
  const searchFieldRef = useRef();
  const history = useHistory();
  const [userEntitlements, setUserEntitlements] = useState([]);
  const [searchObj] = useState({});
  const tutorialsRef = useRef();
  const coursesRef = useRef();
  const recentData = useSelector(getIndexContent);
  const [onLoad, setOnLoad] = useState(false);
  const showEducators = useSelector((state) => hasEducators(state, indexData ? indexData[EDC_SLUG_PERSONALIZED_EDUCATORS]?.linkUrl : undefined, filters || []));
  const showCertifications = useSelector((state) => hasCertifications(state, filters || []));
  const industryRef = useRef();
  const certRef = useRef();
  const educatorsRef = useRef();
  const collectionsRef = useRef();
  const userProfileFetched = useSelector(isUserProfileFetched);
  const personalizedContentLoading = useSelector(isPersonalizedContentLoading);
  const learningRecords = useSelector(getLearningRecords);
  const industryFilter = useSelector(getIndustryFilter);
  const capabilitiesFilter = useSelector(getCapabilitiesFilter);
  const productList = useSelector(getProductList);
  const industrySubSegments = useSelector(getIndustrySubSegments);
  const trialEntitlements = useSelector(userTrialEntitlements);
  const localNavRef = useRef();
  const [localNavSelectedTab, setLocalNavSelectedTab] = useState({});
  const [quickFilters, setQuickFilters] = useState([]);
  const lastScrollTop = useRef(0);
  const moreRef = useRef();
  const sortedCoursesModules = sortByChunkSize(modules, courses) || [];
  const sortedTutorialCurated = sortByChunkSize(tutorials, curated) || [];
  const productListOptions = productList?.map((product) => ({
    label: product.text, value: product.value, guid: product.guid,
  })) || [];
  const [curGroup, setCurGroup] = useState('');
  const [isControlWithActiveTrial, setIsControlWithActiveTrial] = useState(false);
  const [showSoftwareSelector, setShowSoftwareSelector] = useState(true);
  const experimentationContext = useSelector(getExperimentationContext);
  const [analyticsProductString, setAnalyticsProductString] = useState('unknown');
  const [newSoftwareSelected, setNewSoftwareSelected] = useState(productSelected?.guid);
  const haveSelectedProductInEntitlement = productSelected && entitlements && entitlements.findIndex((item) => item.guid === productSelected.guid);
  const isUserSignedIn = (authChecked && userid);
  const [edcLoaded, setEdcLoaded] = useState(false);
  const [showIRLCP, setShowIRLCP] = useState(false);

  useEffect(() => {
    let newProductString = 'unknown';
    if (!onLoad) {
      if (entitlements?.length > 0) {
        newProductString = entitlements.map((e) => e.displayName).join(', ');
      } else if (productSelected) {
        newProductString = productSelected?.text || 'unknown';
      }

      if (analyticsProductString !== newProductString) {
        setAnalyticsProductString(newProductString);
      }
    }
  }, [productSelected, entitlements]);

  const buildNav = () => {
    const navMap = [
      {
        menuItemName: 'Collections',
        render: !!collections?.length > 0,
        component: IndexCarouselSection,
        data: {
          componentData: collections,
          editorialBlock: indexData[(isUserSignedIn && curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT) ? EDC_SLUG_PERSONALIZED_COLLECTIONS : EDC_SLUG_INDEX_COLLECTIONS] ?? {},
          id: 'collections',
          analyticsObject: {
            linkSection: `Homepage Collections for ${analyticsProductString}`,
          },
        },
        ref: collectionsRef,
      },
      {
        menuItemName: 'Tutorials and curated lists',
        render: !!sortedTutorialCurated?.length > 0,
        component: IndexCarouselSection,
        data: {
          learningRecords,
          componentData: sortedTutorialCurated,
          editorialBlock: indexData[isUserSignedIn && curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT ? EDC_SLUG_PERSONALIZED_TUTORIALS : EDC_SLUG_INDEX_TUTORIALS] ?? {},
          id: 'tutorialsAndCuratedLists',
          analyticsObject: {
            linkSection: `Homepage tutorials and curated lists for ${analyticsProductString}`,
          },
        },
        ref: tutorialsRef,
      },
      {
        menuItemName: 'Courses and modules',
        render: !!sortedCoursesModules?.length > 0,
        component: IndexCarouselSection,
        data: {
          componentData: sortedCoursesModules,
          editorialBlock: indexData[isUserSignedIn && curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT ? EDC_SLUG_PERSONALIZED_COURSES : EDC_SLUG_INDEX_COURSES] ?? {},
          id: 'coursesAndModules',
          analyticsObject: {
            linkSection: `Homepage courses and modules for ${analyticsProductString}`,
          },
        },
        ref: coursesRef,
      },
      {
        menuItemName: 'Industry and capabilities',
        render: (industryFilter?.selections?.length > 0 || capabilitiesFilter?.selections?.length > 0),
        component: IndexIndustry,
        data: {
          industryFilter,
          capabilitiesFilter,
          editorialBlock: indexData[EDC_SLUG_INDEX_INDUSTRY],
          id: 'industryAndCapabilities',
          analyticsObject: {
            // linkSection: `Homepage Industry and capabilities for ${selectedProduct?.text}`,
          },
        },
        ref: industryRef,
      },
      {
        menuItemName: 'Certification',
        render: showCertifications,
        component: IndexEdBlock,
        data: {
          editorialBlock: indexData[isUserSignedIn && curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT ? EDC_SLUG_PERSONALIZED_CERTIFICATIONS : EDC_SLUG_INDEX_CERTIFICATIONS] ?? {},
          id: 'certification',
          env,
          analyticsObject: {
            // linkSection: `Homepage Certifications for ${selectedProduct?.text}`,
          },
        },
        ref: certRef,
      },
      {
        menuItemName: 'For education',
        render: showEducators,
        component: IndexEdBlock,
        data: {
          editorialBlock: indexData[isUserSignedIn && curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT ? EDC_SLUG_PERSONALIZED_EDUCATORS : EDC_SLUG_INDEX_EDUCATORS] ?? {},
          id: 'educators',
          env,
          reverse: true,
          analyticsObject: {
            // linkSection: `Homepage Educator content for ${selectedProduct?.text}`,
          },
        },
        ref: educatorsRef,
      },
      {
        menuItemName: 'More learning offerings',
        render: true,
        component: IndexMoreLearning,
        data: {
          editorialBlocks: [indexData[EDC_SLUG_INDEX_MORE_LEARNING_BLOCK3], indexData[EDC_SLUG_INDEX_MORE_LEARNING_BLOCK1], indexData[EDC_SLUG_INDEX_MORE_LEARNING_BLOCK2]],
          id: 'more',
          env,
          analyticsObject: {
            // linkSection: 'Homepage more learning offerings',
          },
        },
        ref: moreRef,
      },
    ];
    return navMap;
  };
  const [nav, setNav] = useState(buildNav());

  useEffect(() => {
    if (!localNavSelectedTab || !localNavSelectedTab.selectedTabId) {
      const validTabs = nav.filter((ni) => ni.render);
      if (validTabs.length > 1) {
        setLocalNavSelectedTab({ validTabs, selectedTabId: validTabs[0].data.id, index: 0 });
      }
    }
  }, [nav, analyticsProductString]);

  useEffect(() => {
    if (Object.keys(indexData).length && !edcLoaded) {
      setEdcLoaded(true);
    }
  }, [indexData, edcLoaded]);

  useEffect(() => {
    if (authChecked && userid) {
      dispatch(fetchLearningRecords({
        uid: userid, env, appVersion, generateIndexGrouping: true, oa2Token,
      }));
    }
  }, [authChecked]);

  useEffect(() => {
    if (curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT && userProfileFetched && entitlements?.length) {
      const userProducts = entitlements.map((product) => ({ checked: true, label: product.displayName, guid: product.guid }));
      setQuickFilters(userProducts);
    }
  }, [userProfileFetched, curGroup]);

  const search = (e) => {
    e?.preventDefault();
    dispatch(setInitiatedSearch(true));
    const searchField = searchFieldRef.current.querySelector('input');
    searchField.value = DOMPurify.sanitize(searchField.value);
    const selectedFilters = quickFilters && quickFilters.length > 0 && quickFilters.filter((filter) => filter.checked);
    const searchFieldTerm = searchField.value ? `/${searchField.value}` : '';
    const entitlementsPath = selectedFilters.length > 0
      ? `/product/${selectedFilters.map((item) => item.label).join(',')}`
      : '';

    history.push(`/catalog${searchFieldTerm}${entitlementsPath}`);

    getRecentSearchTerms(searchField.value);
  };

  useEffect(() => {
    triggerGenericPageView({ pageName: 'Homepage' });
    dispatch(clearResults({ newTerm: '' }));
    dispatch(clearFilters());
  }, []);

  const handleQuickFilterChange = (e) => {
    const curFilter = e.target.value;
    const cloneFilters = [...quickFilters];
    const updatedFilters = cloneFilters.map((filter) => {
      if (filter.label === curFilter) {
        triggerManualLinkClick({ linkTitle: `Quick filter ${filter.label} ${filter.checked ? 'unchecked' : 'checked'}` });
        return { ...filter, checked: !filter.checked };
      }
      return filter;
    });
    setQuickFilters(updatedFilters);
  };

  function positionCheck() {
    const st = window.pageYOffset || document.documentElement.scrollTop;
    const filteredNav = nav?.filter((ni) => ni.data.id === localNavSelectedTab?.selectedTabId);
    if (filteredNav.length > 0) {
      const currentNavRef = filteredNav[0].ref;
      if (currentNavRef?.current && localNavRef.current) {
        if (st < lastScrollTop.current) {
          // scrolling up - switch when current element leaves the window OR previous element top passes localnav bottom
          if (localNavSelectedTab.index > 0) {
            const isBelowWindow = st + window.innerHeight - currentNavRef.current.offsetTop - Number.parseInt(localNavRef.current.style.top, 10) < 1;
            let previousIndex = localNavSelectedTab.index - 1;
            const isNextTopVisible = localNavSelectedTab.validTabs[previousIndex].ref?.current?.offsetTop >= localNavRef.current.offsetTop + localNavRef.current.offsetHeight - 60;
            if (isNextTopVisible) {
              while (previousIndex >= 0 && localNavSelectedTab.validTabs[previousIndex]?.ref?.current?.offsetTop >= localNavRef.current.offsetTop + localNavRef.current.offsetHeight - 60) {
                previousIndex -= 1;
              }
              previousIndex += 1;
              setLocalNavSelectedTab({ index: previousIndex, validTabs: localNavSelectedTab.validTabs, selectedTabId: localNavSelectedTab.validTabs[previousIndex].data.id });
            } else if (isBelowWindow) {
              previousIndex = localNavSelectedTab.index - 1;
              setLocalNavSelectedTab({ index: previousIndex, validTabs: localNavSelectedTab.validTabs, selectedTabId: localNavSelectedTab.validTabs[previousIndex].data.id });
            }
          }
        } else if (st > lastScrollTop.current) {
          // scroll down - switch when current item bottom passes localnav bottom
          const isPassedNav = currentNavRef.current.offsetTop + currentNavRef.current.offsetHeight < localNavRef.current.offsetTop + localNavRef.current.offsetHeight;
          if (isPassedNav) {
            const { index, validTabs } = localNavSelectedTab;
            let nextIndex = index + 1;
            while (validTabs && validTabs.length > nextIndex && localNavSelectedTab.validTabs[nextIndex].ref?.current.offsetTop + localNavSelectedTab.validTabs[nextIndex].ref?.current.offsetHeight < localNavRef.current.offsetTop + localNavRef.current.offsetHeight) {
              nextIndex += 1;
            }
            if (localNavSelectedTab?.index !== nextIndex) {
              setLocalNavSelectedTab({
                index: nextIndex,
                validTabs: localNavSelectedTab.validTabs,
                selectedTabId: localNavSelectedTab.validTabs[nextIndex].data.id,
              });
            }
          }
        }
      }
    }
    lastScrollTop.current = st;
  }

  useEffect(() => {
    window.addEventListener('scroll', positionCheck);
    positionCheck();

    return () => {
      window.removeEventListener('scroll', positionCheck);
    };
  }, [localNavSelectedTab]);

  useEffect(() => {
    setNav(buildNav());
  }, [collections, productSelected, indexData, tutorialsRef, coursesRef, collectionsRef, industryRef, certRef, educatorsRef, learningRecords, analyticsProductString]);

  useEffect(() => {
    const hasEntitlements = (userEntitlements.length && curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT);
    const curSelectedEntitlements = userEntitlements.map((e) => e.guid);
    const prodValues = userEntitlements.map((e) => e.value);
    if (
      (productSelected && window?.location.hash !== `#${newSoftwareSelected}`)
      || (hasEntitlements && !onLoad && !Object.keys(personalizedData).length)
      || (hasEntitlements && window?.location.hash)
    ) {
      const data = {
        product: hasEntitlements ? curSelectedEntitlements : [productSelected?.guid], // entitlements
        productValue: hasEntitlements ? prodValues : [productSelected?.value],
        env,
      };
      dispatch(fetchAllPersonalizedContent(data));
      setNewSoftwareSelected(productSelected?.guid);
      setOnLoad(true);
    }
  }, [dispatch, productSelected, userProfileFetched, userEntitlements, curGroup]);

  useEffect(() => {
    if (quickFilters?.length) {
      // filtering based on available product list
      const userProducts = quickFilters.reduce((acc, filter) => {
        if (productListOptions.find((product) => product.guid === filter.guid)) {
          acc.push({ guid: filter.guid, value: filter.label });
        }
        return acc;
      }, []);
      setUserEntitlements(userProducts);
    }
  }, [quickFilters]);

  useEffect(() => {
    const segment = getExperimentationSegment(experimentationContext);
    if (segment?.segmentType === PERSONALIZATION_EXPERIMENT_TREATMENT
      || (segment?.segmentType === PERSONALIZATION_EXPERIMENT_CONTROL && (segment.segmentKey === PERSONALIZATION_EXPERIMENT_ACTIVETRIALONLY || segment.segmentKey === PERSONALIZATION_EXPERIMENT_ACTIVETRIALANDCOMM))) {
      setIsControlWithActiveTrial(true);
    }

    if (segment?.segmentType === PERSONALIZATION_EXPERIMENT_TREATMENT) {
      setCurGroup(PERSONALIZATION_EXPERIMENT_TREATMENT);
    } else if (segment?.segmentType === PERSONALIZATION_EXPERIMENT_CONTROL) {
      setCurGroup(PERSONALIZATION_EXPERIMENT_CONTROL);
    }
  }, [experimentationContext]);

  useEffect(() => {
    const segment = getExperimentationSegment(experimentationContext);
    if (segment.segmentKey === INDUSTRY_RELEVANT_LEARNING_AND_CERT_PREP) {
      if (segment.segmentValue === IRLCP_EXPERIMENT_CONTROL) {
        setShowIRLCP(false);
      } else if (segment.segmentValue === IRLCP_EXPERIMENT_TREATMENT) {
        setShowIRLCP(true);
      }
    }
  }, [experimentationContext]);

  useEffect(() => {
    if (authChecked) {
      triggerContentImpression({
        containerType: 'irlcphomepage', containerName: `${showIRLCP ? 'treatment' : 'control'}`, containerLocation: `${!userid ? 'signedout' : 'signedin'}`,
      });
    }
  }, [authChecked, showIRLCP, userid]);

  useEffect(() => {
    if (curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT && !entitlements?.length) {
      setShowSoftwareSelector(true);
    } else if (curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT && entitlements?.length) {
      setShowSoftwareSelector(false);
    }
  }, [entitlements, curGroup]);

  useEffect(() => {
    if (authChecked && curGroup) {
      const data = {
        env, isPreview, appVersion, personalised: !!(authChecked && userid && curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT),
      };
      dispatch(fetchIndexData(data)).then((res) => {
        if (res.error) {
          logger.error('ERROR fetching personalized index data: ', res.err);
          dispatch(setErrorState({ type: ERROR_TYPE_XHR }));
        }
      });
    }
  }, [authChecked, userid, curGroup]);

  const RecentActivity = () => (recentData?.length && learningRecords && Object.keys(learningRecords).length > 0 ? (
    <RecActElement className="maxWidth">
      <IndexRecentActivity env={env} learningRecords={learningRecords} showBuyNowTrialPanel={isControlWithActiveTrial} recentData={recentData} trialEntitlements={trialEntitlements && trialEntitlements.length ? trialEntitlements : []} />
    </RecActElement>
  ) : <></>);

  return (
    <PageWrapper>
      <Helmet key="helmet">
        <title>Autodesk Learning</title>
      </Helmet>
      {edcLoaded ? (
        <RootElement>
          <div className={`introHeader ${(curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT) ? PERSONALIZATION_EXPERIMENT_TREATMENT : PERSONALIZATION_EXPERIMENT_CONTROL} ${isUserSignedIn ? 'auth' : 'unauth'}`}>
            <div className="maxWidth">
              <Typography className="maxCopy " dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getChildProperty(indexData, `${EDC_SLUG_INDEX_HEADER}.content`)) }} />
              {(!isUserSignedIn) && (
                <Button
                  variant="outlined"
                  className="signin-btn"
                  onClick={() => LoginClickHandler()}
                >
                  Sign in and start learning
                </Button>
              )}
            </div>
          </div>

          <SearchElement className="maxWidth">
            <Grid container>
              <Grid xs={12}>
                <Typography className="title-header" component="h2" variant="header-copy-medium">
                  {(isUserSignedIn && curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT)
                    ? (
                      <>
                        What will you learn today,
                        {' '}
                        {first}
                        ?
                      </>
                    )
                    : <Typography className="maxCopy" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getChildProperty(indexData, `${EDC_SLUG_CATALOG_INTRO}.content`)) }} />}
                </Typography>
              </Grid>
              <Grid xs={12}>
                {curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT && quickFilters.length !== 0
                && (
                  <Grid container className="quick-filters">
                    <Grid auto>
                      {' '}
                      <Typography variant="button-label">Quick filters:</Typography>
                    </Grid>
                    <Grid md={6} xs={12}>
                      {quickFilters.map((filter) => filter.label && (
                        <FormControlLabel
                          checked={filter.checked}
                          label={filter.label}
                          control={(
                            <Checkbox
                              size="small"
                              id={filter.label}
                              name={filter.label}
                              value={filter.label}
                              onChange={handleQuickFilterChange}
                            />
                          )}
                        />
                      ))}
                    </Grid>
                  </Grid>
                )}

              </Grid>
              <Grid xs={12}>
                <SearchBox
                  result={searchObj}
                  searchCallback={search}
                  searchFieldRef={searchFieldRef}
                  analyticsArea="homepage"
                  placeHolder={curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT ? 'Search the learning catalog...' : 'Search...'}
                />
              </Grid>
            </Grid>
          </SearchElement>

          {recentData?.length && learningRecords ? (
            <div className="maxWidth">
              <Divider />
            </div>
          ) : null}

          {!isUserSignedIn && edcLoaded && (
            <>
              {showIRLCP && (
                <DiscoverIndustryPanels
                  indexData={indexData}
                  industrySubSegments={industrySubSegments}
                />
              )}
              {!showIRLCP && (
                <div className="introParagraph">
                  <div className="maxWidth">
                    <Grid container>
                      <Grid xs={12}>
                        <Typography className="maxCopy " style={{ 'margin-bottom': '32px' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getChildProperty(indexData, `${EDC_SLUG_INDEX_WAYFINDING_INTRO}.content`)) }} />
                      </Grid>
                      <Grid sm={12} md={12} lg={6} className="cardGrid">
                        <IndexEdCard edBlock={indexData[EDC_SLUG_INDEX_WAYFINDING_BLOCK1]} env={env} />
                      </Grid>
                      <Grid sm={12} md={12} lg={6} className="cardGrid">
                        <IndexEdCard edBlock={indexData[EDC_SLUG_INDEX_WAYFINDING_BLOCK2]} env={env} />
                      </Grid>
                      <Grid sm={12} md={12} lg={6} className="cardGrid">
                        <IndexEdCard edBlock={indexData[EDC_SLUG_INDEX_WAYFINDING_BLOCK3]} env={env} />
                      </Grid>
                      <Grid sm={12} md={12} lg={6} className="cardGrid">
                        <IndexEdCard edBlock={indexData[EDC_SLUG_INDEX_WAYFINDING_BLOCK4]} env={env} />
                      </Grid>
                    </Grid>
                  </div>
                </div>
              )}
            </>
          )}

          {isUserSignedIn && (
            <RecentActivity />
          )}
          {showSoftwareSelector && (
            <div className="maxWidth" id="selectProductContainer">
              <SoftwareSelector
                showFreeTrial={(
                  isUserSignedIn && haveSelectedProductInEntitlement < 0)
                  || (!isUserSignedIn && productSelected && Object.keys(productSelected).length > 0)}
              />
            </div>
          )}

          {(isUserSignedIn && userEntitlements?.length && curGroup === PERSONALIZATION_EXPERIMENT_TREATMENT) ? (
            <div className="maxWidth browse-title">
              <Typography
                component="p"
                color="ink-on-background"
                sx={{ maxWidth: '860px' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getChildProperty(indexData, `${EDC_SLUG_PERSONALIZED_SELECT_PRODUCT}.content`)) }}
              />
            </div>
          ) : <></>}

          {
            !personalizedContentLoading && quickStarts?.length ? (
              <div className="maxWidth" id="quickStartGuide" style={quickStarts?.length > 1 ? { paddingBottom: '64px' } : null}>
                <GetStartedPanel data={quickStarts} singleDisplay={quickStarts.length === 1} />
              </div>
            ) : <></>
          }
          {!personalizedContentLoading ? nav?.filter((navItem) => navItem.render).length > 1 && (
            <div id="localNav" ref={localNavRef} data-selected-id={localNavSelectedTab?.selectedTabId}>
              <div className="maxWidth">
                <Tabs
                  styleVariant="quiet"
                  onChange={(e) => {
                    const navItem = nav.find((n) => e.target.id.includes(n.data.id));
                    const ref = navItem?.ref;
                    if (ref) {
                      window.scrollTo(0, ref?.current?.offsetTop + 110);
                    }
                    triggerManualLinkClick({ linkTitle: `${e?.currentTarget?.textContent} for ${analyticsProductString}` || 'unknown' });
                  }}
                  value={localNavSelectedTab?.selectedTabId}
                >
                  {nav?.map((navItem) => navItem.render && <Tab key={navItem.data.id} id={`tab-${navItem.data.id}`} value={navItem.data.id} label={navItem.menuItemName} />)}
                </Tabs>
              </div>
            </div>
          ) : null}

          {personalizedContentLoading && (
            <div className="maxWidth">
              <Skeleton style={{ height: '66px', marginBottom: '16px' }} />
              <Skeleton style={{ height: '70px', marginBottom: '16px' }} />
              <Skeleton style={{ height: '300px', marginBottom: '16px' }} />
              <Skeleton style={{ height: '70px', marginBottom: '16px' }} />
              <Skeleton style={{ height: '300px', marginBottom: '16px' }} />
              <Skeleton style={{ height: '70px', marginBottom: '16px' }} />
              <Skeleton style={{ height: '300px', marginBottom: '16px' }} />
              <Skeleton style={{ height: '70px', marginBottom: '16px' }} />
              <Skeleton style={{ height: '300px', marginBottom: '16px' }} />
            </div>
          )}
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          {!personalizedContentLoading ? nav?.map((navItem) => navItem.render && <navItem.component {...navItem.data} passRef={navItem.ref} selectedProduct={productSelected || { value: userEntitlements?.map((item) => item.value).join(',') }} />) : null}
          <div />

        </RootElement>
      ) : (
        <LoaderRootElement>
          <Skeleton width={30} height={30} />
          <Skeleton width={30} height={30} />
          <br />
          <Skeleton width={100} height={20} />
          <br />
          <Skeleton width={300} height={30} />
          <Skeleton width={200} height={30} />
          <br />
          <Skeleton className="text-lines" count={5} width={600} height={10} />
          <Skeleton className="text-lines" count={1} width={300} height={10} />
          <br />
          <br />
        </LoaderRootElement>
      )}
    </PageWrapper>
  );
};

const RootElement = styled.div`
      
      
      h1 {
        font-size: 34px;
        margin-bottom: 0px;
      }
    
      h2 {
        font-size: 26px;
        margin-bottom: 0px;
      }
    
      h3 {
        font-size: 21px;
        margin-bottom: 0px; 
      }
    
      .browse-title {
        margin-top: 90px;
        margin-bottom: 60px;
        
        p + p {
          margin-top: 16px;
        } 
      }
      .search-with-start-trial {
        display: flex;
        flex-direction: row;
        gap: 48px;
        align-items: center;
  
      }
      .start-trial-wrapper {
        display: flex;
        flex-direction: column;
        align-items: start;
        gap: 8px;
        padding: 0px !important;
        padding-left: 32px !important;
        border-left: 1px solid ${styleVariables.lightDivider}
      }
    
  
      .get-started-panel-card-3-limit-variant-wrapper {
        display: flex;
        flex-direction: column;
        gap: 32px;
    
        .get-started-panel-card-3-limit-variant {
          display: grid;
          grid-template-columns: auto auto auto;
          gap: 24px;
        }
      }
    
    
      .maxCopy {
        max-width: 860px;
      }
  
      .sign-in {
        background-color: ${styleVariables.background};
        color: ${styleVariables.inkOnBackground};
      }
      
      .cardLink, .cardGrid,  .cardBody {
        display:flex;
        flex-direction: column;
        width:100%;
      }
      .cardLink:nth-child(odd) > a{
        padding-left: 1.5rem;
      }
    
      .cardContent {
        h1, h2, h3, h4 {
          margin-top: 0px;
        }
      }
    
      .cardLink, .cardBody, .cardContent {
        flex:1;
      }
      
      .cardLink {
        height: 100%;
      }
    
      .searchBox {
        padding: 48px 0;
        
        h2 {
          font-size: 26px;
           margin-bottom: 20px; 
        }
        
        p p {
          margin-top: 0px;
          
          &:last-child {
            margin-bottom: 32px;
          }
        }
      }
      
      .introParagraph {
        background-color: ${styleVariables.backgroundPanelColor};
        padding: 48px 0 75px;
        margin-bottom: 48px;

        .cardGrid{
          padding-bottom: 1.5rem;
        }
        .cardGrid:nth-child(odd){
          padding-left: 1.5rem;
        }
      }

      .introParagraph .cardActions, #more {
          button {
          align-items: center;
        }
        }
    
      #productLogo {
        padding-top: 20px;
        padding-bottom: 20px;
      }
      #quickStartGuide {
        padding-bottom: 40px;
        
        p {
          margin-bottom: 12px;
          max-width: 800px;
        }
      }
      div#selectProductContainer {
        padding-top: 90px;
         padding-bottom: 60px;
      }
    
      #industryAndCapabilities {
        padding-top: 60px;
        padding-bottom: 60px;
      }
    
    
      #localNav {
        position: sticky;
        background-color: ${styleVariables.background};
        top: 0;
        z-index: 90;
    
        .DhigTabs--styleVariant--quiet {
          overflow: hidden;
        }
    
      }

      .introHeader {
        background-image: url('/learn/static/images/autodesk-learning-banner-3200x680.jpg');
        background-color: #0a0a0a;
        background-size: cover;
        background-repeat: no-repeat;
        padding: 64px 0;

        &.treatment {
          &.auth {
            display: none;
          }
        }
        p {
          color: ${styleVariables.background};
    
        p {
            margin-top: 4px;
          }
        }
      .signin-btn {
        background: ${styleVariables.background};
        margin-top: 8px;
      }
      p {
        color: ${styleVariables.background};
  
          p {
            margin-top: 4px;
          }
        }
      }
    
    
      .sideBySide {
        display: flex;
        min-height: 384px;
        .section {
          &:first-child {
            padding-right: 24px;
          }
          &:last-child {
            padding-left: 24px;
          }
          flex: 1;
          display: flex;
          align-content: center;
          flex-wrap: wrap;
          background-position: center center;
          background-size: cover;
          align-content: center;
    
          p p {
            margin: 12px 0;
          }
        }
    
        &.reverse {
          flex-direction: row-reverse;
          .section:first-child {
            padding-left: 24px;
            padding-right: 0px;
          }
          .section:last-child {
            padding-right: 24px;
            padding-left: 0px;
          }     
        }
      }
    
      .featuredContent {
        padding: 48px 0;
      }
    
      .carouselGroupButton {
        padding-top: 12px;
        padding-left: 0px;

        a {
          align-items: center;
        }
      }
    
    
      .localNav {
        margin-bottom: 64px;
        padding: 16px 0px;
        background: ${styleVariables.backgroundPanelColor};
        box-shadow: ${styleVariables.divider0BottomBoxShadow};
      }
    
      > .panelColor {
        padding-top: 90px;
        margin-top: 30px;
        
        ~ .panelColor {
          margin-top: 0px;
        }
      }
    
    
      @media only screen and (max-width: 560px) {
  
        .search-with-start-trial {
          display: flex;
          flex-direction: column;
          align-items: start;
          gap: 32px;
        }
    
        .start-trial-wrapper {
          padding: 0px !important;
          border: none;
        }
      }
    
      @media only screen and (max-width: 1039px) {
        .sideBySide, .sideBySide.reverse {
            flex-direction: column;
            .section {
              min-height: 248px;
              margin: 0;
              &:first-child {
                margin-bottom: 24px;
              }
            }
    
        }
        .introParagraph {
          .cardGrid{
            padding-bottom: 1.5rem;
          }
          .cardGrid:nth-child(odd){
            padding-left: 0;
          }
        }
      }
    
      @media only screen and (max-width: 800px) {
        h1 {
          font-size: 25px;
        }
    
        h2 {
          font-size: 21px;
        }
    
        h3, .introParagraphBlocks .block h3 {
          font-size: 18px;
        }
      }
    `;

const RecActElement = styled.div`
    margin: 46px 0;
    padding: 46px 0;
    `;

const SearchElement = styled.div`
    margin: 46px auto !important;
    .MuiGrid-root.MuiGrid-item {
      padding-left: 0;
    }
      .title-header{
        margin-bottom: 30px;
      }
      h1 {
        padding-left: 141px
      }
      .quick-filters{
        margin-bottom: 18px;
        
        .MuiTypography-button-label{
          margin: 6px 12px 10px 0;
      }
    }
    `;

const LoaderRootElement = styled.div`
    width: 1440px;
    margin: 94px auto;
    .react-loading-skeleton {
      margin-right: 15px;
      margin-bottom: 8px;
    }
      `;

Index.propTypes = {
  location: PropTypes.shape({}).isRequired,
};

export default Index;
